import React, { useContext } from "react"
import { Box, Stack, Tile, Typography, useTheme } from "@carrotfertility/carotene-core"
import { useIntl } from "react-intl"
import { useWatch } from "@carrotfertility/carotene-core-x"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import {
  IntendedCarrierValues,
  RelatedToStorageFeesValues,
  WhoWillReceiveCareValues
} from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"

export function ThisIsADifferentExpenseTypeBox({ name }: { name: string }): JSX.Element {
  const value = useWatch({ name })
  const {
    formState: { journeyType },
    serverState: { hasElectivePreservationCoverage, hasGestationalCarrierCoverage }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const intl = useIntl()
  const fertilityToPreservation =
    journeyType === JourneyTypes.FERTILITY_CARE &&
    hasElectivePreservationCoverage &&
    value === RelatedToStorageFeesValues.POST_FIRST_YEAR_STORAGE

  const fertilityToGc =
    journeyType === JourneyTypes.FERTILITY_CARE &&
    hasGestationalCarrierCoverage &&
    (value === WhoWillReceiveCareValues.GESTATIONAL_CARRIER || value === IntendedCarrierValues.GESTATIONAL_CARRIER)

  if (fertilityToPreservation) {
    return (
      <ThisIsADifferentExpenseTypeWrapper
        title={intl.formatMessage({ defaultMessage: "This is a fertility preservation expense." })}
        description={intl.formatMessage({
          defaultMessage:
            "When you continue, we’ll update your expense type from fertility care to fertility preservation. If you believe you’re seeing this message in error, please edit your answer to this question."
        })}
        imageSrc="/images/fertility-preservation-expense-grey-bg.png"
      />
    )
  }

  if (fertilityToGc) {
    return (
      <ThisIsADifferentExpenseTypeWrapper
        title={intl.formatMessage({ defaultMessage: "This is a gestational surrogacy expense." })}
        description={intl.formatMessage({
          defaultMessage:
            "When you continue, we’ll update your expense type from fertility care to gestational surrogacy. If you believe you’re seeing this message in error, please edit your answer to this question."
        })}
        imageSrc="/images/gestational-surrogacy-expense-grey-bg.png"
      />
    )
  }

  return null
}

function ThisIsADifferentExpenseTypeWrapper({
  title,
  description,
  imageSrc
}: {
  title: string
  description: string
  imageSrc: string
}): JSX.Element {
  const theme = useTheme()
  return (
    <Tile>
      <Stack direction="row" spacing={theme.tokens.spacing.md} alignItems="flex-start">
        <Box component="img" src={imageSrc} alt={title} />
        <Box>
          <Typography variant="h5">{title}</Typography>
          <Typography sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>{description}</Typography>
        </Box>
      </Stack>
    </Tile>
  )
}
